import React from 'react';
import { Input, Button, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { trLower } from '../../utils/trText';

// Diğer ekranlardaki gibi kolon başına arama (Ant filterDropdown deseni)
// 26.10 düzeltmesi: isteğe bağlı getValue(record) → aranan metin. Verilirse arama
// ham record[dataIndex] yerine bunu kullanır (ör. ekranda '01.10.2026' görünen
// ama ham değeri '2026-10-01' olan Dönem kolonu). Verilmezse davranış aynıdır.
export function getColumnSearchProps(dataIndex, getValue) {
  const oku = typeof getValue === 'function' ? getValue : (record) => record[dataIndex];
  return {
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          autoFocus
          placeholder="Ara..."
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ width: 180, marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button type="primary" size="small" icon={<SearchOutlined />} onClick={() => confirm()}>Ara</Button>
          <Button size="small" onClick={() => { if (clearFilters) clearFilters(); confirm(); }}>Temizle</Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value, record) => {
      const v = oku(record);
      return v != null && trLower(String(v)).includes(trLower(String(value)));
    },
  };
}

// Sayı / tarih / metin akıllı sıralama (TR locale)
// 26.10 düzeltmesi: isteğe bağlı getValue(record) → sıralama anahtarı (sayı, ISO tarih
// ya da metin). Verilmezse record[dataIndex] kullanılır (eski davranış).
export function smartSorter(dataIndex, getValue) {
  const oku = typeof getValue === 'function' ? getValue : (record) => record[dataIndex];
  return (a, b) => {
    const x = oku(a);
    const y = oku(b);
    if (x == null && y == null) return 0;
    if (x == null) return -1;
    if (y == null) return 1;
    const nx = Number(x);
    const ny = Number(y);
    if (!Number.isNaN(nx) && !Number.isNaN(ny) && x !== '' && y !== '') return nx - ny;
    return String(x).localeCompare(String(y), 'tr');
  };
}

/**
 * Bir kolon dizisindeki veri kolonlarına (dataIndex'i olanlara) sıralama + arama ekler.
 * Aksiyon kolonları (dataIndex yok) ve opt-out (col.noFilter) atlanır.
 *
 * 26.10 düzeltmesi: kolon isteğe bağlı `filterValue(record) → string` ve
 * `sortValue(record)` verebilir. Verilirse arama / sıralama bunları kullanır; dataIndex'i
 * OLMAYAN ama bunları veren kolonlar (ör. render ile birleştirilmiş Dönem, hesaplanan
 * Durum) da sıralama/arama alır. Arama için dataIndex ya da filterValue, sıralama için
 * dataIndex ya da sortValue gerekir. Bu alanları vermeyen kolonlarda davranış aynıdır.
 *
 * Ayrıca header/body hizalaması için genişliği OLMAYAN kolonlara varsayılan genişlik +
 * ellipsis verir. Sıralama (`sorter`) başlığı flex bir kapsayıcıya sarar ve arama ikonu
 * ekler; genişlik belirsizse antd'nin colgroup'u header ile body arasında kayar. Tüm
 * veri kolonlarına deterministik genişlik vererek bu kaymayı önleriz (diğer ekranlardaki
 * gibi). opts: { search, sort, defaultWidth }.
 */
/**
 * Kolon genişliklerinin toplamı — sabit başlıklı (scroll.y) tablolarda
 * scroll.x için kullanılır.
 *
 * Neden gerekli: scroll.y verilen tabloda BAŞLIK ve GÖVDE ayrı <table>
 * elemanlarıdır. scroll.x 'max-content' iken ve tabloda fixed kolon varsa
 * rc-table table-layout'u 'auto' seçer (Table.js: "When scroll.x is max-content,
 * no need to fix table layout"); auto modda iki tablo genişliklerini kendi
 * içeriklerinden hesaplar. Başlık hücreleri sıralama okları ve arama ikonu
 * taşıdığı için gövdeden geniş çıkar ve kolonlar sağa doğru artan biçimde
 * kayar. scroll.x SAYISAL verilirse rc-table 'fixed' seçer; her iki tablo da
 * bildirilmiş <col> genişliklerini kullanır ve hizalar.
 *
 * @param {Array} columns - kolon tanımları (enhanceColumns'tan geçmiş olabilir)
 * @param {number} varsayilan - width'i olmayan kolon için varsayılan (enhanceColumns ile aynı)
 */
export function toplamGenislik(columns, varsayilan = 170) {
  return (columns || []).reduce(
    (toplam, c) => toplam + (Number(c && c.width) || varsayilan),
    0
  );
}

export function enhanceColumns(columns, opts = {}) {
  const { search = true, sort = true, defaultWidth = 170 } = opts;
  return (columns || []).map((c) => {
    if (!c || c.noFilter) return c;
    const aramaOku = typeof c.filterValue === 'function' ? c.filterValue : null;
    const siralamaOku = typeof c.sortValue === 'function' ? c.sortValue : null;
    if (!c.dataIndex && !aramaOku && !siralamaOku) return c;
    const extra = {};
    if (sort && c.sorter === undefined && (c.dataIndex || siralamaOku)) {
      extra.sorter = smartSorter(c.dataIndex, siralamaOku);
    }
    if (search && !c.filterDropdown && (c.dataIndex || aramaOku)) {
      Object.assign(extra, getColumnSearchProps(c.dataIndex, aramaOku));
    }
    if (c.width == null) {
      extra.width = defaultWidth;
      if (c.ellipsis == null) extra.ellipsis = true;
    }
    return { ...c, ...extra };
  });
}
